import { useCallback, useEffect, useState } from 'react';
import { buildApiUrl } from '../services/api';

interface Settings {
  faviconUrl?: string;
  pageTitle?: string;
  iconUrl?: string;
  companyName?: string;
}

const STORAGE_KEY = 'public_settings_cache';

let cachedSettings: Settings | null = null;
let inFlightRequest: Promise<Settings | null> | null = null;
const listeners = new Set<(settings: Settings | null) => void>();

const isBrowser = typeof window !== 'undefined';

const readStoredSettings = (): Settings | null => {
  if (!isBrowser) {
    return null;
  }

  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as Settings;
  } catch {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

const writeStoredSettings = (settings: Settings | null) => {
  if (!isBrowser) {
    return;
  }

  if (!settings) {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return;
  }

  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
};

const notifyListeners = (settings: Settings | null) => {
  listeners.forEach((listener) => listener(settings));
};

const setSharedSettings = (settings: Settings | null) => {
  cachedSettings = settings;
  writeStoredSettings(settings);
  notifyListeners(settings);
};

const loadGlobalSettings = async (forceRefresh = false): Promise<Settings | null> => {
  if (!forceRefresh) {
    if (cachedSettings) {
      return cachedSettings;
    }

    const storedSettings = readStoredSettings();
    if (storedSettings) {
      cachedSettings = storedSettings;
      return storedSettings;
    }
  }

  if (inFlightRequest) {
    return inFlightRequest;
  }

  inFlightRequest = (async () => {
    try {
      const response = await fetch(buildApiUrl('/settings/public'));

      if (!response.ok) {
        return cachedSettings ?? readStoredSettings();
      }

      const data = await response.json();
      const nextSettings: Settings = {
        faviconUrl: data.faviconUrl,
        pageTitle: data.pageTitle,
        iconUrl: data.iconUrl,
        companyName: data.companyName
      };

      setSharedSettings(nextSettings);
      return nextSettings;
    } catch (err) {
      console.log('Não foi possível carregar configurações globais:', err);
      return cachedSettings ?? readStoredSettings();
    } finally {
      inFlightRequest = null;
    }
  })();

  return inFlightRequest;
};

export function useGlobalSettings() {
  const [settings, setSettings] = useState<Settings | null>(() => cachedSettings ?? readStoredSettings());

  const refreshSettings = useCallback(async () => {
    const nextSettings = await loadGlobalSettings(true);
    setSettings(nextSettings);
    return nextSettings;
  }, []);

  useEffect(() => {
    listeners.add(setSettings);

    loadGlobalSettings().then((nextSettings) => {
      setSettings(nextSettings);
    });

    return () => {
      listeners.delete(setSettings);
    };
  }, []);

  return {
    settings,
    refetch: refreshSettings
  };
}
